// features/chat-artifact-feature.js — ARTIFACT SESSION ORCHESTRATOR
// ═══════════════════════════════════════════════════════════
// Single-tab viewer for artifact sessions (multi-select synthesis
// threads). Lives inside Artifacts feature.
//
// The Insight / Depth tabs were removed — synthesis threads are a
// single surface now. Raw chat only.
// ═══════════════════════════════════════════════════════════
import { useState, useRef } from 'react';
import { useVoice } from '../hooks/use-voice';
import ShiftThreadHeader from '../components/shift-thread-header';
import ThreadArtifactPanel from '../components/thread-artifact-panel';
import ContentActionMenu from '../components/content-action-menu';
import ChatArtifactRaw from './chat-artifact-raw';

const TYPE_COLORS = {
  web:      '#c8a96e',
  code:     '#c46050',
  document: '#9b8fc4',
  image:    '#7a9e82',
  data:     '#6b8fa8',
  upload:   '#b8956a',
};

export default function ChatArtifactFeature({
  userId, supabase, userRecord,
  artifact, sessionId,
  onBack,
}) {
  const [messages, setMessages] = useState([]);
  const [showArtifactPanel, setShowArtifactPanel] = useState(false);
  const [artifactPanelWidth, setArtifactPanelWidth] = useState(280);
  const artifactContainerRef = useRef(null);
  const inputRef = useRef(null);
  const voiceSourceRef = useRef(false);
  const voiceResponseCallbackRef = useRef(null);

  const { voiceListening, toggleVoice } = useVoice({
    session: null, userRecord, currentSessionId: sessionId,
    threadHistory: [], inputRef,
    setInput: () => {}, send: () => {},
    voiceEnabled: true, voiceSourceRef, voiceResponseCallbackRef,
  });

  const artifactType = (artifact?.artifact_type || '').toLowerCase();
  const isCollection = (artifact?.title || '').includes('items —');
  const badgeColor = isCollection ? '#888' : (TYPE_COLORS[artifactType] || '#c8a96e');
  const badgeLabel = isCollection ? 'Collection' : (artifact?.artifact_type || 'Artifact');
  const titleLabel = artifact?.title?.substring(0, 30) || 'Chat';

  // ── Resize ────────────────────────────────────────────────
  function startResize(e) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = artifactPanelWidth;
    function onMove(e2) {
      const delta = startX - e2.clientX;
      setArtifactPanelWidth(Math.min(600, Math.max(220, startWidth + delta)));
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  return (
    <div ref={artifactContainerRef} style={{
      position: 'relative', height: '100%',
      display: 'flex', flexDirection: 'row', overflow: 'hidden',
      background: '#07070a',
    }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* ── HEADER ── */}
        <ShiftThreadHeader
          feature="Artifact"
          badge={badgeLabel}
          badgeColor={badgeColor}
          tabLabel={titleLabel}
          tabColor="#7a9e82"
          onArtifacts={() => {
            if (!showArtifactPanel) {
              const containerWidth = artifactContainerRef.current?.offsetWidth || 900;
              setArtifactPanelWidth(Math.round(containerWidth * 0.33));
            }
            setShowArtifactPanel(p => !p);
          }}
          artifactsActive={showArtifactPanel}
          actionSlot={
            <ContentActionMenu
              context="thread"
              contentId={sessionId}
              contentType="session"
              messages={messages}
              userId={userId}
              supabase={supabase}
              sessionId={sessionId}
            />
          }
          onClose={onBack}
        />

        {/* ── RAW CHAT ── */}
        <ChatArtifactRaw
          userId={userId} supabase={supabase} userRecord={userRecord}
          artifact={artifact} sessionId={sessionId}
          messages={messages} setMessages={setMessages}
          voiceListening={voiceListening} toggleVoice={toggleVoice}
        />

        {/* Scroll styles */}
        <style jsx>{`
          .messages-scroll::-webkit-scrollbar { width: 3px; }
          .messages-scroll::-webkit-scrollbar-track { background: transparent; }
          .messages-scroll::-webkit-scrollbar-thumb { background: transparent; border-radius: 2px; }
          .messages-scroll:hover::-webkit-scrollbar-thumb { background: #1e1e2a; }
          .messages-scroll { scrollbar-width: thin; scrollbar-color: transparent transparent; }
          .messages-scroll:hover { scrollbar-color: #1e1e2a transparent; }
          [data-placeholder]:empty::before {
            content: attr(data-placeholder);
            color: #444; font-style: italic;
          }
        `}</style>
      </div>

      {showArtifactPanel && (
        <div
          onMouseDown={startResize}
          style={{ width: 4, background: 'var(--border-4)', cursor: 'col-resize', flexShrink: 0, transition: 'background 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = '#c8a96e33'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--border-4)'}
        />
      )}
      {showArtifactPanel && (
        <ThreadArtifactPanel
          userId={userId}
          supabase={supabase}
          currentSessionId={sessionId}
          width={artifactPanelWidth}
          onArtifactSelect={() => {
            const containerWidth = artifactContainerRef.current?.offsetWidth || 900;
            setArtifactPanelWidth(Math.round(containerWidth * 0.50));
          }}
        />
      )}
    </div>
  );
}
